import React, { useState } from 'react';
import { addDays, addWeeks, addMonths, addYears } from 'date-fns';
import { Lead } from '../../types';
import { useI18n } from '../../context/I18nContext';
import { LEAD_STATUS_I18N_KEYS } from '../../utils/statusUtils';
import BaseFormModal from '../ui/BaseFormModal';
import Button from '../ui/Button';
import DatePicker from '../ui/DatePicker';

interface FollowUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lead: Lead, newDate: Date) => void;
  lead: Lead | null;
}

const FollowUpModal: React.FC<FollowUpModalProps> = ({
  isOpen,
  onClose,
  onSave,
  lead,
}) => {
  const { t } = useI18n();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  React.useEffect(() => {
    if (lead && lead.nextActionAt) {
      const date = new Date(lead.nextActionAt);
      setSelectedDate(date);
    } else {
      // Default date: tomorrow
      const tomorrow = addDays(new Date(), 1);
      setSelectedDate(tomorrow);
    }
  }, [lead]);

  const handleSave = () => {
    if (!lead || !selectedDate) return;

    // Set time to 9:00 AM by default if no time is set
    const newDate = new Date(selectedDate);
    if (!newDate.getHours() && !newDate.getMinutes()) {
      newDate.setHours(9, 0, 0, 0);
    }
    
    // Close modal immediately for snappy UX
    onClose();
    
    // Save in background
    Promise.resolve(onSave(lead, newDate)).catch(error => {
      console.error('Error saving follow-up:', error);
    });
  };

  const handleClose = () => {
    setSelectedDate(null);
    onClose();
  };

  const setQuickDate = (date: Date) => {
    setSelectedDate(date);
  };

  const getStatusLabel = (status: Lead['status']) =>
    t(LEAD_STATUS_I18N_KEYS[status] || 'status.unknown') || status;


  if (!lead) return null;

  return (
    <BaseFormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('leads.rescheduleTitle')}
      size="md"
      onValidate={handleSave}
    >
      <div className="space-y-6">
        {/* Lead Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">{lead.name}</h3>
          <p className="text-sm text-gray-600">{lead.contact_value || t('leads.noContact')}</p>
          <div className="flex items-center space-x-4 mt-2">
            <span className="text-sm text-gray-500">
              <strong>{t('leads.status')}:</strong> {getStatusLabel(lead.status)}
            </span>
          </div>
        </div>

        {/* New Date Selection */}
        <div className="space-y-4">
          <DatePicker
            label={t('leads.newFollowUpDate')}
            value={selectedDate}
            onChange={setSelectedDate}
            placeholder={t('leads.newFollowUpDate')}
          />

          {/* Quick Suggestions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('leads.quickSuggestions')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDate(addDays(new Date(), 1))}
              >
                {t('leads.tomorrow')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDate(addWeeks(new Date(), 1))}
              >
                {t('leads.nextWeek')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDate(addMonths(new Date(), 1))}
              >
                {t('leads.nextMonth')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDate(addYears(new Date(), 1))}
              >
                {t('leads.nextYear')}
              </Button>
            </div>
          </div>
        </div>

        {/* Actions handled by BaseFormModal footer */}
      </div>
    </BaseFormModal>
  );
};

export default FollowUpModal;